import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { cnpj, cpf } from 'cpf-cnpj-validator';
import { ConfirmationService, PrimeNGConfig, SharedModule } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DropdownModule } from 'primeng/dropdown';
import { FileUploadModule } from 'primeng/fileupload';
import { InputMaskModule } from 'primeng/inputmask';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { FormFieldComponent } from '../../../shared/components/form-field/form-field.component';
import { EstadosConst } from '../../const/estados.const';
import { PessoaModel } from '../../models/pessoa.model';
import { I18nService } from '../../service/i18n.service';
import { NotificacaoService } from '../../service/notificacao.service';
import { ToolsService } from '../../service/tools.service';
import { CadastroService } from './service/cadastro.service';
@Component({
  selector: 'app-cadastro',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    FormFieldComponent,
    ButtonModule,
    InputTextModule,
    InputMaskModule,
    PasswordModule,
    FileUploadModule,
    ConfirmDialogModule,
    SharedModule,
    DropdownModule,
  ],
  providers: [ConfirmationService],
  templateUrl: './cadastro.component.html',
  styleUrls: ['./cadastro.component.scss'],
})
export class CadastroComponent implements OnInit {
  form!: FormGroup;
  isLoading = signal<boolean>(false);
  filePreviewUrl: string | undefined = undefined;

  // Injeção de dependências
  private fb = inject(FormBuilder);
  private cadastroService = inject(CadastroService);
  private translate = inject(TranslateService);
  private i18nService = inject(I18nService);
  private router = inject(Router);
  private notificacaoService = inject(NotificacaoService);
  private confirmationService = inject(ConfirmationService);
  private primeNGConfig = inject(PrimeNGConfig);

  // Validação de senha
  hasLowerCase = signal<boolean>(false);
  hasUpperCase = signal<boolean>(false);
  hasNumber = signal<boolean>(false);
  hasSpecialChar = signal<boolean>(false);
  hasMinLength = signal<boolean>(false);
  regexSenha = '^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})';

  get senhaValida(): boolean {
    return (
      this.hasLowerCase() &&
      this.hasUpperCase() &&
      this.hasNumber() &&
      this.hasSpecialChar() &&
      this.hasMinLength()
    );
  }
  get senhasIguais(): boolean {
    const senha = this.form.get('seguranca.senha')?.value;
    const confirmarSenha = this.form.get('seguranca.confirmarSenha')?.value;
    return senha && confirmarSenha && senha === confirmarSenha;
  }

  estados = EstadosConst.sigla;
  constructor() {
    this.translate.use(this.i18nService.idioma.value.code);
  }

  ngOnInit() {
    this.criarFormulario();
    this.configurarValidacaoSenha();
    this.configurarTraducaoPrimeNG();
  }
  private configurarTraducaoPrimeNG(): void {
    this.primeNGConfig.setTranslation({
      weak: 'Fraca',
      medium: 'Média',
      strong: 'Forte',
      passwordPrompt: 'Digite uma senha',
    });
  }

  // Substitua o método anterior por este:

  criarFormulario() {
    this.form = this.fb.group({
      // Dados Pessoais
      nomeCompleto: ['', [Validators.required, this.nomeSobrenomeValidator]],
      cpfCnpj: ['', [Validators.required, this.cpfCnpjValidator]],
      email: ['', [Validators.required, Validators.email]],
      telefone: ['', Validators.required],
      redeSocial: [''],
      website: [''],

      // Upload de imagem
      imagemPerfil: [''],
      typeImage: [''],

      // Segurança
      seguranca: this.fb.group(
        {
          senha: [
            '',
            [
              Validators.required,
              Validators.minLength(6),
              this.validadorSenhaCompleta,
            ],
          ],
          confirmarSenha: ['', Validators.required],
        },
        { validators: this.validadorSenhasIguais }
      ),

      // Endereço
      endereco: this.fb.group({
        codPostal: ['', Validators.required],
        logradouro: ['', Validators.required],
        numero: ['', Validators.required],
        complemento: [''],
        bairro: ['', Validators.required],
        cidade: ['', Validators.required],
        estado: ['', Validators.required],
        pais: ['Brasil', Validators.required],
        latitude: [''],
        longitude: [''],
      }),
    });
  }

  // Validações customizadas
  nomeSobrenomeValidator(control: any) {
    const nomeCompleto = control.value?.trim();
    if (!nomeCompleto || nomeCompleto.split(' ').length < 2) {
      return { nomeIncompleto: true };
    }
    return null;
  }

  cpfCnpjValidator(control: any) {
    const valor = control.value;
    if (!valor) return null;

    const limpo = ToolsService.removerMascara(valor);
    if (!cpf.isValid(limpo) && !cnpj.isValid(limpo)) {
      return { cpfCnpjInvalido: true };
    }
    return null;
  }

  // Formatação de CPF/CNPJ
  formatarCpfCnpj(event: Event) {
    const input = event.target as HTMLInputElement;
    const valor = ToolsService.removerMascara(input.value);

    if (valor.length <= 11) {
      input.value = this.formatarCPF(valor);
    } else {
      input.value = this.formatarCNPJ(valor);
    }

    this.form.get('cpfCnpj')?.setValue(input.value);
  }

  private formatarCPF(cpf: string): string {
    return cpf
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }

  private formatarCNPJ(cnpj: string): string {
    return cnpj
      .replace(/^(\d{2})(\d)/, '$1.$2')
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1/$2')
      .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
  }

  // Busca de CEP
  async buscarCep(event: Event) {
    const input = event.target as HTMLInputElement;
    const cep = ToolsService.removerMascara(input.value);
    if (cep.length === 8) {
      this.cadastroService.buscarCep(cep).then((data: any) => {
        debugger;
        if (data && !data.erro) {
          this.form.patchValue({
            endereco: {
              logradouro: data.logradouro || '',
              bairro: data.bairro || '',
              cidade: data.localidade || '',
              estado: data.uf || '',
              pais: 'Brasil',
            },
          });
        }
      });
    }
  }

  // Upload de imagem
  imagemSelecionada(event: any, fileUpload: any): void {
    const file = event.files[0];

    if (file) {
      // Validar tamanho e tipo
      if (file.size > 10000000) {
        // 10MB
        this.notificacaoService.notificacaoError(
          'Arquivo muito grande. Máximo 10MB.'
        );
        fileUpload.clear();
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        const base64String = reader.result as string;
        this.form.patchValue({
          imagemPerfil: base64String.split(',')[1],
          typeImage: file.type,
        });
        this.filePreviewUrl = base64String;
      };

      reader.readAsDataURL(file);
    }

    fileUpload.clear();
  }

  removerImagem() {
    this.filePreviewUrl = undefined;
    this.form.patchValue({
      imagemPerfil: '',
      typeImage: '',
    });
  }

  // Limpeza de campos
  private removerEspacos(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);

      if (control instanceof FormControl && typeof control.value === 'string') {
        const trimmed = control.value.trim();
        control.setValue(trimmed, { emitEvent: false });
      } else if (control instanceof FormGroup) {
        this.removerEspacos(control);
      }
    });
  }

  // Submissão do formulário
  salvar() {
    this.removerEspacos(this.form);
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      this.notificacaoService.notificacaoWarn(
        'Por favor, corrija os erros no formulário.'
      );
      return;
    }

    this.isLoading.set(true);

    const usuario = this.montarUsuario();

    this.cadastroService
      .adicionarUsuario(usuario)
      .then(() => {
        this.confirmationService.confirm({
          header: this.translate.instant(
            'PAGINA_CADASTRO.TITULO_SUCESSO_SALVAR_USUARIO'
          ),
          message: this.translate.instant(
            'PAGINA_CADASTRO.MENSSAGEM_SUCESSO_ADICIONAR_USUARIO'
          ),
          accept: () => {
            this.router.navigate(['/login']);
          },
        });
      })
      .catch((error) => {
        const mensagem = this.translate.instant(
          'PAGINA_CADASTRO.CONSULTE_SUPORTE'
        );

        if (error.status === 409) {
          // Formatar como texto simples com quebras de linha
          const errosTexto = error.error.errors.join(' | ');

          const mensagemCompleta = `${errosTexto}`;

          this.notificacaoService.notificacaoError(mensagemCompleta, 8000); // Mais tempo para ler
        }

        if (error.status === 500) {
          this.notificacaoService.notificacaoError(
            `Erro: ${error.status} ${mensagem}`
          );
        }
      })
      .finally(() => {
        this.isLoading.set(false);
      });
  }

  private montarUsuario(): PessoaModel {
    const formValue = this.form.value;

    return {
      id: 0,
      nome: formValue.nomeCompleto,
      cpfCnpj: ToolsService.removerMascara(formValue.cpfCnpj),
      email: formValue.email,
      telefone: ToolsService.removerMascara(formValue.telefone),
      website: formValue.website || '',
      redeSocial: formValue.redeSocial || '',
      idioma: null,
      imagemPerfil: formValue.imagemPerfil || '',
      typeImage: formValue.typeImage || '',
      seguranca: {
        id: 0,
        senha: formValue.seguranca.senha,
        confirmarSenha: formValue.seguranca.confirmarSenha,
      },
      endereco: {
        id: 0,
        logradouro: formValue.endereco.logradouro,
        numero: formValue.endereco.numero,
        bairro: formValue.endereco.bairro,
        cidade: formValue.endereco.cidade,
        estado: formValue.endereco.estado,
        complemento: formValue.endereco.complemento || '',
        pais: formValue.endereco.pais,
        codPostal: ToolsService.removerMascara(formValue.endereco.codPostal),
        latitude: null,
        longitude: null,
      },
    };
  }

  cancelar() {
    this.router.navigate(['/login']);
  }

  private configurarValidacaoSenha(): void {
    // Monitora mudanças na senha para validação em tempo real
    this.form
      .get('seguranca.senha')
      ?.valueChanges.subscribe((senha: string) => {
        this.validarRequisitosSenha(senha);
      });

    // Monitora mudanças na confirmação de senha
    this.form.get('seguranca.confirmarSenha')?.valueChanges.subscribe(() => {
      this.validarConfirmacaoSenha();
    });
  }

  // ========================================
  // VALIDADORES CUSTOMIZADOS
  // ========================================
  private validadorSenhasIguais = (formGroup: FormGroup) => {
    const senha = formGroup.get('seguranca.senha')?.value;
    const confirmarSenha = formGroup.get('seguranca.confirmarSenha')?.value;

    if (senha && confirmarSenha && senha !== confirmarSenha) {
      formGroup
        .get('seguranca.confirmarSenha')
        ?.setErrors({ passwordsMismatch: true });
      return { passwordsMismatch: true };
    }

    return null;
  };

  // ========================================
  // MÉTODOS DE VALIDAÇÃO
  // ========================================
  private validarRequisitosSenha(senha: string): void {
    if (!senha) {
      this.resetarValidacaoSenha();
      return;
    }

    this.hasLowerCase.set(/[a-z]/.test(senha));
    this.hasUpperCase.set(/[A-Z]/.test(senha));
    this.hasNumber.set(/[0-9]/.test(senha));
    this.hasSpecialChar.set(/[!@#$%^&*]/.test(senha));
    this.hasMinLength.set(senha.length >= 8);
  }

  private resetarValidacaoSenha(): void {
    this.hasLowerCase.set(false);
    this.hasUpperCase.set(false);
    this.hasNumber.set(false);
    this.hasSpecialChar.set(false);
    this.hasMinLength.set(false);
  }

  private validarConfirmacaoSenha(): void {
    const senha = this.form.get('seguranca.senha')?.value;
    const confirmarSenha = this.form.get('seguranca.confirmarSenha')?.value;

    if (confirmarSenha && senha !== confirmarSenha) {
      this.form
        .get('seguranca.confirmarSenha')
        ?.setErrors({ passwordsMismatch: true });
    } else if (confirmarSenha && senha === confirmarSenha) {
      // Remove apenas o erro de senhas diferentes, mantendo outros erros se existirem
      const errors = this.form.get('seguranca.confirmarSenha')?.errors;
      if (errors) {
        delete errors['passwordsMismatch'];
        const hasOtherErrors = Object.keys(errors).length > 0;
        this.form
          .get('seguranca.confirmarSenha')
          ?.setErrors(hasOtherErrors ? errors : null);
      }
    }
  }
  private validadorSenhaCompleta = (control: any) => {
    const senha = control.value;
    if (!senha) return null;

    this.validarRequisitosSenha(senha);

    if (!this.senhaValida) {
      return { senhaIncompleta: true };
    }

    return null;
  };
}
